import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Cog,
  FileText,
  FlaskConical,
  Battery,
  Truck,
  ShieldCheck,
  HandshakeIcon,
  Flag,
  Coins,
  ClipboardCheck,
} from "lucide-react";

const charterGoals = [
  {
    icon: Cog,
    title: "Sustainable Mobility Technologies",
    description:
      "Facilitating innovation, R&D, testing and market incubation for sustainable mobility solutions.",
  },
  {
    icon: FileText,
    title: "Policy Making & Stakeholder Engagement",
    description:
      "Mobility related policy development and key stakeholder engagement with the Government sector.",
  },
  {
    icon: FlaskConical,
    title: "R&D and Testing",
    description:
      "All key aspects of research, development, and testing for the Mobility sector.",
  },
  {
    icon: Battery,
    title: "Sustainable Energy Innovation",
    description:
      "Supporting all energy-related innovations that are sustainable and eco-friendly.",
  },
  {
    icon: Truck,
    title: "Last Mile Connectivity",
    description:
      "Focus on last mile connectivity and rural mobility solutions for underserved areas.",
  },
  {
    icon: ShieldCheck,
    title: "Safety Initiatives",
    description:
      "Developing and implementing safety initiatives in partnership with the Government.",
  },
  {
    icon: HandshakeIcon,
    title: "Government Programme Support",
    description:
      "Facilitating India Government intervention in developing new mobility programmes.",
  },
  {
    icon: Flag,
    title: "International Support",
    description:
      "Supporting overseas Governments in India and their trade delegations.",
  },
  {
    icon: Coins,
    title: "Facilitating Finance",
    description:
      "Connecting mobility projects and innovations with appropriate financing sources.",
  },
  {
    icon: ClipboardCheck,
    title: "Programme Management",
    description:
      "Comprehensive programme management for mobility development projects.",
  },
];

const CharterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="charter"
      ref={ref}
      data-testid="charter-section"
      className="py-20 md:py-32 bg-stone-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-4 block">
            Our Charter
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mb-4 tracking-tight">
            Key Goals & Focus Areas
          </h2>
          <p className="text-stone-600 leading-relaxed">
            These goals form the basis of developing activities, events, and core
            annual programmes with primary focus on sustainable mobility
            technologies.
          </p>
        </motion.div>

        {/* Charter Goals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {charterGoals.map((goal, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              className="charter-item flex items-start gap-4 p-5 rounded-xl bg-white border border-stone-100 hover:border-emerald-200 hover:shadow-md transition-all"
              data-testid={`charter-goal-${index}`}
            >
              <div className="flex-shrink-0 p-2.5 bg-emerald-50 rounded-lg">
                <goal.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-stone-900 mb-1">
                  {goal.title}
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed">
                  {goal.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CharterSection;
